import * as Configuration from './configuration'
import * as Constants from './constants'
import * as Hooks from './hooks'
import * as I18n from '@rushplay/i18n'
import * as Icons from './icons'
import * as R from 'ramda'
import * as React from 'react'
import * as ReactRedux from 'react-redux'
import * as ThemeUi from 'theme-ui'

import {Image} from './image'
import PropTypes from 'prop-types'
import Url from 'url'
import kebabCase from 'lodash.kebabcase'

export function PaymentMethod(props) {
  const locale = ReactRedux.useSelector(Configuration.getLocale)
  const amountCents = ReactRedux.useSelector(Configuration.getAmountCents)
  const unacceptableFixedValue =
    R.includes(props.value, R.keys(Constants.acceptableFixedValues)) &&
    !R.includes(amountCents, Constants.acceptableFixedValues[props.value])

  const minAmount = Hooks.useToAmount({
    amountCents: props.minCents,
    minimumFractionDigits: '0',
  })
  const maxAmount = Hooks.useToAmount({
    amountCents: props.maxCents,
    minimumFractionDigits: '0',
  })
  const fixedValues = Hooks.useToArrayOfAmounts({
    amountCents: R.pathOr([], [props.value], Constants.acceptableFixedValues),
    minimumFractionDigits: '0',
  })

  const disabled =
    unacceptableFixedValue ||
    R.lt(props.maxCents, amountCents) ||
    R.gt(props.minCents, amountCents)

  const imageTranslationKey = `payment-method.${kebabCase(props.id)}`
  const requiredPSPInfo = `payment-method.${kebabCase(props.id)}.required-info`
  const transactionWithTranslationKey = `transaction-with.${kebabCase(
    props.id
  )}`

  const translate = I18n.useTranslate(
    () => [imageTranslationKey, requiredPSPInfo, transactionWithTranslationKey],
    [props.id]
  )

  const imageSource = Url.parse(translate(imageTranslationKey) || '')

  return (
    <ThemeUi.Box
      as="label"
      sx={{
        'cursor': disabled ? 'not-allowed' : 'pointer',
        'width': '100%',
        'flexDirection': 'column',
        'justifyContent': 'center',
        'backgroundColor': props.maskedAccount ? '#1D2230' : 'transparent',
        'borderRadius': props.maskedAccount ? '8px' : 'none',
        'color': 'muted',
        'fontSize': '8px',
        'overflow': 'hidden',
        'display': 'flex',
        'flexGrow': '1',
        'transition':
          'transform 125ms ease-in-out, border-color 125ms ease-in-out',
        '&:hover': {
          backgroundColor: !disabled && '#1D2230',
        },
        '&:active': {
          transform: !disabled && 'scale(0.975)',
        },
        '&:focus-within': {
          outlineColor: 'primary',
          outlineStyle: 'auto',
        },
      }}
    >
      <ThemeUi.Box
        as="input"
        sx={{
          opacity: 0,
          zIndex: '-1',
          overflow: 'hidden',
          position: 'absolute',
          width: '0',
          height: '0',
        }}
        checked={props.selected === props.value}
        disabled={disabled}
        name={props.name}
        type="radio"
        value={props.value}
        onChange={() => props.onChange(props.value)}
      />
      <ThemeUi.Box
        sx={{
          display: 'flex',
          width: '100%',

          justifyContent: !props.maskedAccount ? 'space-between' : 'initial',
          alignItems: 'center',
          color: 'paymentMethodText',
          pl: 1,
          py: props.maskedAccount ? '8px' : '14px',
          filter: disabled && 'grayscale(1)',
        }}
      >
        <ThemeUi.Box
          sx={{
            width: props.maskedAccount ? '120px' : 'auto',
            maxHeight: props.maskedAccount ? '28px' : '20px',
          }}
        >
          <Image
            alt={props.id}
            source={imageSource.pathname || ''}
            resizingHeight={props.maskedAccount ? 28 : 20}
            sx={{
              height: props.maskedAccount ? '28px' : '20px',
              filter: (theme) => theme.colors.paymentMethodImageFilter,
            }}
          />
        </ThemeUi.Box>

        {props.maskedAccount || props.lastSuccess ? (
          <ThemeUi.Grid
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              color: '#BBC6E4',
              gridGap: '0px',
            }}
          >
            {props.maskedAccount && (
              <ThemeUi.Text>{props.maskedAccount}</ThemeUi.Text>
            )}
            {props.lastSuccess && (
              <ThemeUi.Text sx={{alignSelf: 'flex-end'}}>
                {translate('payment-method.last-used', {
                  date: new Date(
                    R.replace(/-/g, '/', props.lastSuccess)
                  ).toLocaleDateString(locale, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  }),
                })}
              </ThemeUi.Text>
            )}
          </ThemeUi.Grid>
        ) : (
          <ThemeUi.Flex sx={{fontSize: '14px', alignItems: 'center'}}>
            {translate(transactionWithTranslationKey)}
            <ThemeUi.Box sx={{transform: 'rotate(180deg)', fontSize: 2, px: 0}}>
              <Icons.NavigateBack />
            </ThemeUi.Box>
          </ThemeUi.Flex>
        )}
      </ThemeUi.Box>

      {translate(requiredPSPInfo)}

      {(unacceptableFixedValue ||
        R.gt(props.minCents, amountCents) ||
        R.lt(props.maxCents, amountCents)) && (
        <ThemeUi.Box
          sx={{
            bg: 'info',
            color: 'white',
            p: '4px',
            wordBreak: 'keep-all',
            fontWeight: 'bold',
            fontStyle: 'normal',
            fontSize: 0,
            display: 'grid',
            alignItems: 'center',
            gridGap: '4px',
            gridTemplateColumns: 'max-content 1fr',
          }}
        >
          <Icons.Info />
          {unacceptableFixedValue &&
            translate('payment-method.acceptable-amounts', {
              amount: R.map((value) => ` ${value}`, fixedValues),
            })}
          {R.gt(props.minCents, amountCents) &&
            translate('payment-method.min-amount', {amount: minAmount})}
          {R.lt(props.maxCents, amountCents) &&
            translate('payment-method.max-amount', {amount: maxAmount})}
        </ThemeUi.Box>
      )}
    </ThemeUi.Box>
  )
}

PaymentMethod.propTypes = {
  feePercent: PropTypes.number,
  id: PropTypes.string.isRequired,
  lastSuccess: PropTypes.string,
  maskedAccount: PropTypes.string,
  maxCents: PropTypes.number.isRequired,
  minCents: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  selected: PropTypes.string,
  type: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
}

I18n.Loader.preload(
  [
    'payment-method.acceptable-amounts',
    'payment-method.min-amount',
    'payment-method.max-amount',
    'payment-method.fee',
    'payment-method.last-used',
    'payment-method.japan-credit-card-ftd',
  ],
  PaymentMethod
)
